import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useConfirm } from '@/hooks/useConfirm';
import { useSetOrgIsMsp } from '@/hooks/useOrganizationMsp';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

interface Props {
  organizationId: string | null;
  isMsp: boolean;
  /** Whether the current user holds `org:update` — the permission the PATCH requires. */
  canEdit: boolean;
}

/**
 * MSP designation toggle, embedded in OrganizationSettings.
 *
 * Turning this on opens the MSP surfaces: customer dashboards and customer
 * management, where divisions tagged as customers are managed. Turning it off is
 * non-destructive — it hides those surfaces but leaves every division, tag, and
 * piece of customer data intact, so the switch is safely reversible.
 */
export default function OrgMspSection({ organizationId, isMsp, canEdit }: Props) {
  const setIsMsp = useSetOrgIsMsp(organizationId);
  const { confirm, confirmDialog } = useConfirm();

  const handleChange = (next: boolean) => {
    if (!next) {
      void confirm({
        title: 'Turn off MSP mode?',
        message:
          'Customer dashboards and customer management will be hidden. Your divisions, tags, and customer data are kept — turning MSP mode back on restores everything.',
        confirmText: 'Turn off',
      }).then((ok) => {
        if (ok) setIsMsp.mutate(false);
      });
      return;
    }
    setIsMsp.mutate(true);
  };

  return (
    <div>
      <h3 className="text-base font-semibold mb-1">Managed service provider</h3>
      <p className="text-sm text-muted-foreground mb-3">
        Turn this on if you manage networks on behalf of customers. Divisions can then be
        designated as customers, and you get a portfolio dashboard across all of them.
      </p>

      <div className="flex items-center gap-3">
        <Switch
          id="org-is-msp"
          checked={isMsp}
          onCheckedChange={handleChange}
          disabled={!canEdit || setIsMsp.isPending || !organizationId}
          aria-describedby={canEdit ? undefined : 'org-is-msp-help'}
        />
        <Label htmlFor="org-is-msp" className="cursor-pointer">
          {isMsp ? 'MSP mode is on' : 'MSP mode is off'}
        </Label>
      </div>

      {!canEdit ? (
        <p id="org-is-msp-help" className="text-xs text-muted-foreground mt-2">
          You need the <code>org:update</code> permission to change this.
        </p>
      ) : null}

      {setIsMsp.isError ? (
        <p className="text-xs text-destructive mt-2 inline-flex items-center gap-1.5" role="alert">
          <AlertCircle className="w-3.5 h-3.5" />
          Save failed.
        </p>
      ) : null}
      {setIsMsp.isSuccess ? (
        <p className="text-xs text-sev-4 mt-2 inline-flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Saved.
        </p>
      ) : null}

      {confirmDialog}
    </div>
  );
}
